// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GuildSticker} from '@app/features/expressions/models/GuildSticker';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import {StickerPickerStateSchema} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/pickers_pb';
import {makeAutoObservable} from 'mobx';

type StickerKeyInput = Readonly<Pick<GuildSticker, 'guildId' | 'id'>>;
type StickerUsageEntry = Readonly<{
	count: number;
	lastUsed: number;
}>;

const MAX_FRECENT_STICKERS = 21;
const FRECENCY_TIME_DECAY_HOURS = 24 * 7;
const logger = new Logger('StickerPicker');

class StickerPicker {
	stickerUsage: Record<string, StickerUsageEntry> = {};
	favoriteStickers: Array<string> = [];
	collapsedCategories: Array<string> = [];

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
	}

	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'stickerPicker',
			schema: StickerPickerStateSchema,
			persist: ['stickerUsage', 'favoriteStickers', 'collapsedCategories'],
			toMessage: (s) => ({
				usage: Object.fromEntries(
					Object.entries(s.stickerUsage).map(([key, entry]) => [
						key,
						{count: entry.count, lastUsedMs: BigInt(entry.lastUsed)},
					]),
				),
				favoriteStickerIds: [...s.favoriteStickers],
				collapsedCategoryIds: [...s.collapsedCategories],
			}),
			applyMessage: (s, m) => {
				const usage: Record<string, StickerUsageEntry> = {};
				for (const [key, stat] of Object.entries(m.usage)) {
					usage[key] = {count: stat.count, lastUsed: Number(stat.lastUsedMs)};
				}
				s.stickerUsage = usage;
				s.favoriteStickers = [...m.favoriteStickerIds];
				s.collapsedCategories = [...m.collapsedCategoryIds];
			},
			mergeForMigration: (local, server) => {
				const usage: Record<
					string,
					{
						count: number;
						lastUsedMs: bigint;
					}
				> = {};
				for (const [key, stat] of Object.entries(local.usage)) {
					usage[key] = {count: stat.count, lastUsedMs: stat.lastUsedMs};
				}
				for (const [key, stat] of Object.entries(server.usage)) {
					const existing = usage[key];
					if (existing === undefined) {
						usage[key] = {count: stat.count, lastUsedMs: stat.lastUsedMs};
					} else {
						usage[key] = {
							count: Math.max(existing.count, stat.count),
							lastUsedMs: existing.lastUsedMs > stat.lastUsedMs ? existing.lastUsedMs : stat.lastUsedMs,
						};
					}
				}
				return {
					usage,
					favoriteStickerIds: Array.from(new Set([...local.favoriteStickerIds, ...server.favoriteStickerIds])),
					collapsedCategoryIds: Array.from(new Set([...local.collapsedCategoryIds, ...server.collapsedCategoryIds])),
				};
			},
		});
	}

	trackStickerUsage(stickerKey: string): void {
		const now = Date.now();
		const currentUsage = this.stickerUsage[stickerKey];
		const newCount = (currentUsage?.count ?? 0) + 1;
		this.stickerUsage[stickerKey] = {
			count: newCount,
			lastUsed: now,
		};
	}

	toggleFavorite(stickerKey: string): void {
		if (this.favoriteStickers.includes(stickerKey)) {
			const index = this.favoriteStickers.indexOf(stickerKey);
			if (index > -1) {
				this.favoriteStickers.splice(index, 1);
			}
		} else {
			this.favoriteStickers.push(stickerKey);
		}
		ComponentDispatch.dispatch('STICKER_PICKER_RERENDER');
		logger.debug(`Toggled favorite sticker: ${stickerKey}`);
	}

	toggleCategory(category: string): void {
		if (this.collapsedCategories.includes(category)) {
			const index = this.collapsedCategories.indexOf(category);
			if (index > -1) {
				this.collapsedCategories.splice(index, 1);
			}
		} else {
			this.collapsedCategories.push(category);
		}
		ComponentDispatch.dispatch('STICKER_PICKER_RERENDER');
		logger.debug(`Toggled category: ${category}`);
	}

	isFavorite(sticker: StickerKeyInput): boolean {
		return this.favoriteStickers.includes(this.getStickerKey(sticker));
	}

	isCategoryCollapsed(categoryId: string): boolean {
		return this.collapsedCategories.includes(categoryId);
	}

	private getFrecencyScore(entry: StickerUsageEntry): number {
		const now = Date.now();
		const hoursSinceLastUse = (now - entry.lastUsed) / (1000 * 60 * 60);
		const timeDecay = Math.max(0, 1 - hoursSinceLastUse / FRECENCY_TIME_DECAY_HOURS);
		return entry.count * (1 + timeDecay);
	}

	getFrecentStickers(
		allStickers: ReadonlyArray<GuildSticker>,
		limit: number = MAX_FRECENT_STICKERS,
	): Array<GuildSticker> {
		const stickerScores: Array<{
			sticker: GuildSticker;
			score: number;
		}> = [];
		for (const sticker of allStickers) {
			const stickerKey = this.getStickerKey(sticker);
			const usage = this.stickerUsage[stickerKey];
			if (usage) {
				const score = this.getFrecencyScore(usage);
				stickerScores.push({sticker, score});
			}
		}
		stickerScores.sort((a, b) => b.score - a.score);
		const result = stickerScores.slice(0, limit).map((item) => item.sticker);
		return result;
	}

	getFavoriteStickers(allStickers: ReadonlyArray<GuildSticker>): Array<GuildSticker> {
		const favorites: Array<GuildSticker> = [];
		for (const sticker of allStickers) {
			if (this.isFavorite(sticker)) {
				favorites.push(sticker);
			}
		}
		return favorites;
	}

	getFrecencyScoreForSticker(sticker: StickerKeyInput): number {
		const usage = this.stickerUsage[this.getStickerKey(sticker)];
		return usage ? this.getFrecencyScore(usage) : 0;
	}

	private getStickerKey(sticker: StickerKeyInput): string {
		return `${sticker.guildId}:${sticker.id}`;
	}
}

export default new StickerPicker();
